import { sheetToPreview, type GeoSheetSource, type GeoSnapPoint } from '@entities/geodesy';

/**
 * Прилипание точки привязки к векторной графике листа. Всё в пикселях превью
 * (viewBox оверлея); экранные размеры переводятся множителем `k` — пикселей
 * превью на один CSS-пиксель при текущем зуме.
 */

/** Радиус прилипания в экранных пикселях. */
export const SNAP_RADIUS_PX = 14;

export type SnapGlyph = 'cross' | 'x' | 'square' | 'dot';

/** Пересечение осей — «+», пересечение линий — «×», свая — квадрат, прочее — точка. */
export function snapGlyph(kind: GeoSnapPoint['kind']): SnapGlyph {
  switch (kind) {
    case 'axis_cross':
      return 'cross';
    case 'line_cross':
      return 'x';
    case 'pile':
      return 'square';
    default:
      return 'dot';
  }
}

export interface PreviewSnap {
  px: number;
  py: number;
  snap: GeoSnapPoint;
}

export function toPreviewSnaps(sheet: GeoSheetSource, points: GeoSnapPoint[]): PreviewSnap[] {
  const out: PreviewSnap[] = [];
  for (const s of points) {
    if (!Number.isFinite(s.x) || !Number.isFinite(s.y)) continue;
    const { px, py } = sheetToPreview(sheet, s.x, s.y);
    out.push({ px, py, snap: s });
  }
  return out;
}

/** Индекс ближайшей точки не дальше `radius` (пиксели превью) или `null`. */
export function nearestSnap(
  list: ReadonlyArray<PreviewSnap>,
  px: number,
  py: number,
  radius: number,
): number | null {
  let best: number | null = null;
  let bestD2 = radius * radius;
  for (let i = 0; i < list.length; i += 1) {
    const s = list[i];
    if (!s) continue;
    const dx = s.px - px;
    const dy = s.py - py;
    const d2 = dx * dx + dy * dy;
    if (d2 <= bestD2) {
      bestD2 = d2;
      best = i;
    }
  }
  return best;
}

/* Размер значков в экранных пикселях (половина стороны / плечо / радиус). */
const SIZE: Readonly<Record<SnapGlyph, number>> = { cross: 5, x: 3.5, square: 3.5, dot: 2 };

const f = (n: number) => n.toFixed(1);

/** Контур значка в координатах превью; `scale` — множитель размера (×k, ×1.6 у активной). */
export function glyphPath(glyph: SnapGlyph, x: number, y: number, scale: number): string {
  const a = SIZE[glyph] * scale;
  switch (glyph) {
    case 'cross':
      return `M${f(x - a)} ${f(y)}H${f(x + a)}M${f(x)} ${f(y - a)}V${f(y + a)}`;
    case 'x':
      return `M${f(x - a)} ${f(y - a)}L${f(x + a)} ${f(y + a)}M${f(x - a)} ${f(y + a)}L${f(x + a)} ${f(y - a)}`;
    case 'square':
      return `M${f(x - a)} ${f(y - a)}H${f(x + a)}V${f(y + a)}H${f(x - a)}Z`;
    case 'dot':
      return `M${f(x - a)} ${f(y)}a${f(a)} ${f(a)} 0 1 0 ${f(2 * a)} 0a${f(a)} ${f(a)} 0 1 0 ${f(-2 * a)} 0Z`;
  }
}

/**
 * Все значки одним-двумя `path`: линии (кресты, квадраты) и залитые точки. На
 * листе бывают тысячи пересечений — отдельный DOM-узел на каждое не нужен.
 */
export function snapLayerPaths(
  list: ReadonlyArray<PreviewSnap>,
  k: number,
): { strokes: string; dots: string } {
  const strokes: string[] = [];
  const dots: string[] = [];
  for (const s of list) {
    const g = snapGlyph(s.snap.kind);
    (g === 'dot' ? dots : strokes).push(glyphPath(g, s.px, s.py, k));
  }
  return { strokes: strokes.join(''), dots: dots.join('') };
}

/** Координата с графики листа: точная, срезаем только хвост float. */
export function roundExact(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

/**
 * Координата «по картинке»: точнее пикселя превью не бывает — округляем до
 * разрешения превью (не меньше сотых: для PDF в pt это ≈ 0,2 мм листа).
 */
export function roundToPreview(n: number, pxPerUnit: number): number {
  const digits = pxPerUnit > 0 ? Math.min(6, Math.max(2, Math.ceil(Math.log10(pxPerUnit)) + 1)) : 2;
  const m = 10 ** digits;
  return Math.round(n * m) / m;
}
